import React from 'react'
import WeatherIcon from '../../../common/WeatherIcon/WeatherIcon'
import Link from 'next/link'
import type { Daybox } from '@/services/api/bff/weather/dtos/Dayboxes.dto'

export interface DailyBox {
  weekDay: string
  warning?: boolean
}
export type ForecastDaybox = DailyBox & Daybox

export interface ForecastCardProps {
  name: string
  dayDescription?: string
  warning?: string
  warningIndex?: number
  currentSymbol: string
  currentTemp: string
  currentFeltTemp?: string
  now?: boolean
  localTime: string
  weekDay: string
  date?: string
  tMax: number
  tMin: number
  limitedDayboxes: ForecastDaybox[]
  url: string
}

const ForecastCard: React.FC<ForecastCardProps> = (props) => {
  const {
    name,
    dayDescription,
    warning,
    warningIndex = 0,
    currentSymbol,
    currentTemp,
    currentFeltTemp,
    now = true,
    localTime,
    weekDay,
    date,
    tMax,
    tMin,
    limitedDayboxes,
    url,
  } = props

  const isMajorWarning = Boolean(warning && warningIndex === 3)
  const isMediumWarning = Boolean(warning && warningIndex === 2)

  return (
    <Link
      prefetch={false}
      className={`w-full cursor-pointer h-[405px] xl:h-[250px] rounded-lg pl-2 xl:pl-6 pr-2 xl:pr-4 pt-4 pb-2 xl:pb-4 flex flex-col xl:flex-row !no-underline ${
        isMajorWarning ? 'bg-semantic-red' : 'bg-skyblue'
      }`}
      target="_self"
      href={url}
    >
      {/* Left section */}
      <div
        className={`flex flex-col xl:flex-row flex-1 ${isMajorWarning ? 'basis-1/3' : 'basis-2/3'}`}
      >
        <div className={`px-2 xl:px-0 ${!isMajorWarning ? 'basis-1/2' : ''}`}>
          <h3 className="text-white font-bold text-xl xl:text-4xl leading-tight max-w-full xl:max-w-[400px] truncate">
            {name}
          </h3>
          {!isMajorWarning && dayDescription && (
            <p className="text-white font-medium text-base leading-5 m-0 pb-4">
              {dayDescription}
            </p>
          )}
          {isMediumWarning && warning && (
            <div className="bg-semantic-red rounded-lg py-3 px-4 xl:flex text-white hidden overflow-hidden">
              <WeatherIcon name="w1" className="w-5 xl:w-6 mr-4" />
              <span dangerouslySetInnerHTML={{ __html: warning }} />
            </div>
          )}
        </div>

        {/* Current weather (fixed icon/text sizes to avoid reflow) */}
        <div className="flex flex-row justify-evenly pb-7 xl:pb-0 basis-1/2">
          <WeatherIcon
            name={currentSymbol}
            className="w-24 xl:w-36 h-24 xl:h-36 self-center"
          />
          <div className="self-center text-base text-white">
            {now ? (
              <>
                <div>
                  <span className="font-semibold pr-2">Jetzt</span>
                  <span className="text-cloudblue-1 font-semibold">
                    {localTime} Uhr
                  </span>
                </div>
                <div className="text-7xl font-medium leading-none">
                  {currentTemp}°
                </div>
                {currentFeltTemp && (
                  <div className="pt-1 font-medium">{currentFeltTemp}</div>
                )}
              </>
            ) : (
              <>
                <div>
                  <span className="font-semibold">{weekDay}</span>, {date}
                </div>
                <div className="text-6xl font-medium leading-none">
                  {Math.round(tMax)}°
                </div>
                <div className="text-4xl text-cloudblue-1 leading-none">
                  {Math.round(tMin)}°
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Major warning banner */}
      {isMajorWarning && warning && (
        <div className="bg-semantic-red rounded-lg py-3 px-4 xl:flex basis-1/3 text-white items-center hidden">
          <WeatherIcon name="w1" className="w-5 xl:w-8 h-5 xl:h-8 mr-4" />
          <span dangerouslySetInnerHTML={{ __html: warning }} />
        </div>
      )}

      {/* Daily forecast boxes (fixed icon sizes) */}
      <div className="basis-1/3 bg-white rounded pt-4 pb-5 xl:py-10 px-2">
        <div className="sm:flex flex-row justify-evenly h-full items-center hidden">
          {limitedDayboxes?.map((box) => (
            <div
              key={box.weekDay}
              className="flex flex-col items-center h-full justify-between"
            >
              <span className="font-semibold text-sm text-black">
                {box.weekDay}
              </span>
              <div className="flex items-center">
                {box.warning && (
                  <WeatherIcon
                    name="w1"
                    onWhite
                    className="w-5 xl:w-6 h-5 xl:h-6 shrink-0"
                  />
                )}
                <WeatherIcon
                  name={box?.attributes?.symbol}
                  className="w-14 h-14 self-center"
                />
              </div>
              <div className="text-center">
                <span className="font-bold text-xl text-black">
                  {Math.round(box?.attributes.tMax)}°
                </span>
                <span className="font-medium text-base text-grey-400 pl-1">
                  {Math.round(box?.attributes.tMin)}°
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-row justify-evenly h-full items-center sm:hidden">
          {limitedDayboxes?.slice(0, 3).map((box) => (
            <div
              key={box.weekDay}
              className="flex flex-col items-center h-full justify-between"
            >
              <span className="font-semibold text-sm text-black">
                {box.weekDay}
              </span>
              <div className="flex items-center">
                {box.warning && (
                  <WeatherIcon
                    name="w1"
                    onWhite
                    className="w-5 xl:w-6 h-5 xl:h-6 shrink-0"
                  />
                )}
                <WeatherIcon
                  name={box?.attributes?.symbol}
                  className="w-14 h-14 self-center"
                />
              </div>
              <div className="text-center">
                <span className="font-bold text-xl text-black">
                  {Math.round(box?.attributes.tMax)}°
                </span>
                <span className="font-medium text-base text-grey-400">
                  {Math.round(box?.attributes.tMin)}°
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Link>
  )
}

export default ForecastCard
